import { KeyboardEvent, memo, useContext, useEffect, useCallback, useRef } from 'react';

import { color, variable } from '../../helpers/style.helpers';

import { SelectSkeletonContext } from './context';
import {
  ISelectSkeletonContext,
  SelectSkeletonItemProps,
  StyledSelectSkeletonItemProps,
} from './SelectSkeleton.decl';

import { Box } from '@aircall/tractor';
import styled, { css } from '@xstyled/styled-components';
import noop from 'lodash-es/noop';

const StyledSelectItem = styled(Box)<StyledSelectSkeletonItemProps>`
  display: flex;
  align-items: center;
  font-size: ${variable.typography.variants.body.fontSize};
  ${({ grouped }) => !grouped && `border-top: 1px solid ${color.grey.light};`}
  ${({ size, theme }) =>
    size &&
    css`
      min-height: ${theme.textFieldInput.sizes[size].height};
    `};

  &:hover,
  &:focus {
    background-color: ${color.grey.light};
    cursor: pointer;
    outline: none;
  }
  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${color.grey.light};
    `}
`;

export const SelectSkeletonItem = memo(
  ({
    option,
    index,
    disableAutofocus,
    'data-test': dataTest,
    grouped,
  }: SelectSkeletonItemProps) => {
    const { selected, setSelected, closeMenu, onChange, optionRenderer, size } =
      useContext<ISelectSkeletonContext>(SelectSkeletonContext);
    const ref = useRef<HTMLLIElement>(null);
    const { value, label } = option;
    const isActive = selected!.value === value;

    useEffect(() => {
      if (!disableAutofocus && (isActive || index === 0)) {
        const frameId = window.requestAnimationFrame(() => {
          ref.current!.focus();
        });

        return () => {
          window.cancelAnimationFrame(frameId);
        };
      }

      return noop;
    }, [isActive, selected, index, disableAutofocus]);

    const handleClick = useCallback(() => {
      setSelected(option);
      onChange(option);
      closeMenu();
    }, [setSelected, closeMenu, option, onChange]);

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        setSelected(option);
        onChange(option);
        closeMenu();
      }
    };

    const handleFocusOnNearestElement = (nearestElement: HTMLLIElement) => {
      if (nearestElement) {
        nearestElement.focus();
      } else {
        ref.current!.focus();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.key === 'ArrowUp') {
        const prevElement = ref.current!.previousElementSibling as HTMLLIElement;
        handleFocusOnNearestElement(prevElement);
      }

      if (e.key === 'ArrowDown') {
        const nextElement = ref.current!.nextElementSibling as HTMLLIElement;
        handleFocusOnNearestElement(nextElement);
      }
    };

    return (
      <StyledSelectItem
        grouped={grouped}
        as='li'
        tabIndex={0}
        role='option'
        aria-selected={isActive}
        onClick={handleClick}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        isActive={isActive}
        pl='s'
        ref={ref}
        size={size}
        data-test={`${dataTest}-${value}`}
      >
        {optionRenderer ? optionRenderer(option, isActive) : label}
      </StyledSelectItem>
    );
  }
);
